import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { format } from 'date-fns';
import RoleGate from '@/components/layout/RoleGate';

export type CalendarView = 'month' | 'agenda';

interface Props {
  month: Date;
  onMonthChange: (next: Date) => void;
  onToday: () => void;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onNewEvent: () => void;
}

export default function CalendarToolbar({
  month,
  onMonthChange,
  onToday,
  view,
  onViewChange,
  onNewEvent,
}: Props) {
  const prev = () => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const next = () => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1));

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex items-center gap-1">
        <h1 className="mr-2 text-lg font-semibold text-flock-navy-900 lg:w-44">
          {format(month, 'MMMM yyyy')}
        </h1>
        <button
          onClick={prev}
          aria-label="Previous month"
          className="rounded-lg p-1.5 text-flock-navy-400 transition hover:bg-flock-navy-50 hover:text-flock-navy-700"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <button
          onClick={next}
          aria-label="Next month"
          className="rounded-lg p-1.5 text-flock-navy-400 transition hover:bg-flock-navy-50 hover:text-flock-navy-700"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
        <button
          onClick={onToday}
          className="ml-1 rounded-lg border border-flock-gray-200 px-2.5 py-1 text-xs font-medium text-flock-navy-600 transition hover:bg-flock-navy-50"
        >
          Today
        </button>
      </div>

      <div className="hidden gap-1 rounded-full bg-flock-navy-50 p-1 lg:flex">
        {(['month', 'agenda'] as CalendarView[]).map((v) => (
          <button
            key={v}
            onClick={() => onViewChange(v)}
            aria-pressed={view === v}
            className={clsx(
              'rounded-full px-4 py-1 text-xs font-medium capitalize transition',
              view === v
                ? 'bg-flock-navy-600 text-white'
                : 'text-flock-navy-500 hover:text-flock-navy-700'
            )}
          >
            {v}
          </button>
        ))}
      </div>

      <div className="ml-auto">
        <RoleGate minRole="group_leader">
          <button
            onClick={onNewEvent}
            className="min-h-touch flex items-center gap-1.5 rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-flock-navy-700"
          >
            <PlusIcon className="h-4 w-4" /> New Event
          </button>
        </RoleGate>
      </div>
    </div>
  );
}
